import React from "react";
import Cookies from "js-cookie";
import { Dimmer, Loader, Message } from "semantic-ui-react";

import { SetPropNewObj } from "react-lib/frameworks/SetProp";
import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";
import ErrorMessage from "react-lib/apps/common/ErrorMessage";

import DENController from "./DENController";
import { DENHostProps, State, StateInitial } from "./DENInterface";

import "./DEN.scss";

export type DENContext = {
  state: State;
  controller: DENController;
  onEvent: DENController["handleEvent"];
  setProp: (key: string, value: any, callback?: any) => void;
  setState: (state: Partial<State>, callback?: () => void) => void;
};

type DENRootProps = DENHostProps & {
  // false เมื่อ container ของ host scroll ให้อยู่แล้ว
  scrollable?: boolean;
  className?: string;
  // เรียกหลังโหลด patient / encounter เสร็จ
  onLoaded?: (den: DENContext) => void;
  children: (den: DENContext) => React.ReactNode;
};

// ถือ controller และ state ของ card ใน DEN
// เป็น class เพราะ SetPropNewObj และ handler อ่าน state ผ่าน getState()
export default class DENRoot extends React.Component<DENRootProps, State> {
  controller: DENController;
  setProp: DENContext["setProp"];
  isUnmounted = false;

  static defaultProps = {
    scrollable: true,
  };

  constructor(props: DENRootProps) {
    super(props);

    const root = this;

    this.state = StateInitial as State;
    this.controller = new DENController(
      () => this.state,
      (state: any, callback?: () => void) => this.safeSetState(state, callback),
      window
    );

    this.controller.apiToken = props.apiToken || Cookies.get("apiToken") || "";
    this.controller.cookies = Cookies;
    // กัน setState หลัง unmount
    this.setProp = SetPropNewObj(
      {
        get state() {
          return root.state;
        },
        setState: root.safeSetState,
      },
      ""
    );
    this.controller.setProp = this.setProp;
  }

  async componentDidMount() {
    await this.controller.handleEvent({ message: "DidMount", params: this.props });

    this.handleLoaded();
  }

  async componentDidUpdate(prevProps: DENRootProps) {
    const hostKeys = ["django", "userPosition", "currentDoctor", "selectedDivision", "userProfile"] as const;

    if (hostKeys.some((key) => prevProps[key] !== this.props[key])) {
      this.controller.handleEvent({ message: "SetHostProps", params: this.props });
    }

    if (prevProps.patientId !== this.props.patientId || prevProps.encounterId !== this.props.encounterId) {
      await this.controller.handleEvent({
        message: "LoadPatientEncounter",
        params: { patientId: this.props.patientId, encounterId: this.props.encounterId },
      });

      this.handleLoaded();
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  safeSetState = (state: any, callback?: () => void) => {
    if (!this.isUnmounted) {
      this.setState(state, callback);
    }
  };

  getContext = (): DENContext => ({
    state: this.state,
    controller: this.controller,
    onEvent: this.controller.handleEvent,
    setProp: this.setProp,
    setState: (state, callback) => this.safeSetState(state, callback),
  });

  handleLoaded = () => {
    if (!this.isUnmounted) {
      this.props.onLoaded?.(this.getContext());
    }
  };

  handleCloseMessage = () => {
    this.safeSetState({ openModMessage: false, errorMessage: null });
  };

  render() {
    const { state } = this;

    return (
      <div
        className={[
          "den-module",
          this.props.scrollable ? "den-module--scrollable" : "",
          state.selectorMode ? "selector-mode" : "",
          this.props.className || "",
        ].join(" ")}
      >
        <Dimmer active={!state.loadError && (!!state.isMainLoading || !state.masterReady)} inverted>
          <Loader inverted />
        </Dimmer>

        {state.loadError && <Message negative content={state.loadError} />}

        {!state.loadError && this.props.children(this.getContext())}

        <ModConfirmCNMI
          closeIcon={true}
          titleColor={state.errorMessage ? "red" : "green"}
          openModal={!!state.openModMessage}
          titleName={state.messageTitle}
          content={
            typeof state.errorMessage === "string" ? (
              <div
                dangerouslySetInnerHTML={{ __html: state.errorMessage || "" }}
                style={{ textAlign: "center", margin: "-0.25rem 0 -1.5rem" }}
              ></div>
            ) : (
              <ErrorMessage
                error={state.errorMessage || undefined}
                success={state.successMessage || undefined}
                style={{ marginBottom: "-1.5rem" }}
              />
            )
          }
          onDeny={this.handleCloseMessage}
          onApprove={this.handleCloseMessage}
          onCloseWithDimmerClick={this.handleCloseMessage}
          approveButtonText={"ตกลง"}
          approveButtonColor={state.errorMessage ? "red" : "green"}
          denyButtonText={""}
        />
      </div>
    );
  }
}
